import React, { useState } from 'react';
import type { SharedArticleItem } from '../types';

interface Props {
  articles: SharedArticleItem[];
  onOpenPreview: (fileId: string) => void;
  onDownload: (fileId: string) => void;
}

export const SharedArticlesFileView: React.FC<Props> = ({
  articles,
  onOpenPreview,
  onDownload,
}) => {
  const [selectedArticleId, setSelectedArticleId] = useState<string>(
    articles[0]?.id || ''
  );

  const activeArticle =
    articles.find((a) => a.id === selectedArticleId) || articles[0];

  const packageNames = [
    '01 Manuscript',
    '02 Dataset',
    '03 Submission package',
    '04 Review cycle',
    '05 Legal / finance',
    '06 Journal reference',
    '07 Outcome',
  ];

  return (
    <div className="shared-articles-file-view" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
        {articles.map((art) => {
          const isSelected = art.id === activeArticle?.id;
          return (
            <button
              key={art.id}
              type="button"
              onClick={() => setSelectedArticleId(art.id)}
              style={{
                background: isSelected ? 'rgba(168, 85, 247, 0.15)' : 'rgba(15, 23, 42, 0.6)',
                border: `1px solid ${isSelected ? '#a855f7' : 'rgba(148, 163, 184, 0.15)'}`,
                borderRadius: '12px',
                padding: '0.85rem 1.25rem',
                textAlign: 'left',
                minWidth: '280px',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: '0.78rem', color: '#c084fc', fontWeight: 600, marginBottom: '0.2rem' }}>
                {art.article_id} · {art.stage}
              </div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {art.title}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                Дедлайн: {art.nearest_deadline || 'Не установлен'}
              </div>
            </button>
          );
        })}
      </div>

      {activeArticle && (
        <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '16px', padding: '1.5rem' }}>
          <div style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.15)', paddingBottom: '1rem', marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 0.4rem 0' }}>
              {activeArticle.title}
            </h3>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#cbd5e1' }}>
              <span>Журнал: <strong>{activeArticle.journal}</strong></span>
              <span>Текущая рукопись: <strong>{activeArticle.current_manuscript_version || 'v1.0'}</strong></span>
              <span>Статус рецензии: <strong>{activeArticle.review_status || 'В процессе'}</strong></span>
            </div>
          </div>

          <h4 style={{ color: '#f8fafc', fontSize: '1rem', margin: '0 0 1rem 0' }}>
            Файловые пакеты статьи (01–07)
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
            {packageNames.map((pkg) => {
              const filesInPkg = activeArticle.packages[pkg] || [];
              return (
                <div
                  key={pkg}
                  style={{
                    background: 'rgba(30, 41, 59, 0.35)',
                    border: '1px solid rgba(148, 163, 184, 0.12)',
                    borderRadius: '12px',
                    padding: '1rem',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: 600, color: '#f1f5f9', fontSize: '0.88rem' }}>
                      {pkg}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', background: 'rgba(0, 0, 0, 0.3)', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>
                      {filesInPkg.length} файлов
                    </span>
                  </div>

                  {filesInPkg.length === 0 ? (
                    <div style={{ fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic', padding: '0.5rem 0' }}>
                      Файлы в пакете пока отсутствуют
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {filesInPkg.map((f) => (
                        <div
                          key={f.file_id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: 'rgba(15, 23, 42, 0.6)',
                            padding: '0.5rem 0.75rem',
                            borderRadius: '8px',
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#f8fafc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {f.display_name}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                              {f.version_no} · {f.size_label}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <button
                              type="button"
                              className="btn-open-preview"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              onClick={() => onOpenPreview(f.file_id)}
                            >
                              Просмотр
                            </button>
                            <button
                              type="button"
                              className="action-icon-btn"
                              style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              onClick={() => onDownload(f.file_id)}
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
